<!-- components/form/CheckboxControl.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import FieldControl from './FieldControl.vue'

defineOptions({ inheritAttrs: false })

type ValidateOn = 'input' | 'blur' | 'submit'

const props = withDefaults(
  defineProps<{
    /** Vee-Validate field name (obrigatório) */
    name: string

    /** Label simples (se quiser label custom com links, use o slot `label`) */
    label?: string
    required?: boolean
    hint?: string

    /** v-model externo (opcional) */
    modelValue?: unknown

    /** Estratégia de validação (herda do FormContext se omitido) */
    validateOn?: ValidateOn

    /** Valores lógicos custom (ex.: 'Y'/'N', 1/0) */
    trueValue?: unknown
    falseValue?: unknown

    /** Acessibilidade / id */
    forId?: string

    /** Estado */
    disabled?: boolean
    readonly?: boolean

    /** Regras locais do vee-validate (opcional) */
    rules?: unknown
  }>(),
  {
    label: '',
    required: false,
    hint: '',
    modelValue: undefined,
    validateOn: undefined,
    // defaults como factory
    trueValue: () => true,
    falseValue: () => false,
    forId: undefined,
    disabled: false,
    readonly: false,
    rules: undefined,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: unknown): void
  (e: 'focus'): void
  (e: 'blur'): void
}>()

/** Bridge para v-model booleano visível pelo v-checkbox */
const checkedComputed = computed<boolean>({
  get: () => props.modelValue === props.trueValue,
  set: (val: boolean) => {
    emit('update:modelValue', val ? props.trueValue : props.falseValue)
  },
})
</script>

<template>
  <FieldControl
    :name="props.name"
    :model-value="props.modelValue"
    :validate-on="props.validateOn"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rules="props.rules"
    @update:modelValue="(v) => emit('update:modelValue', v)"
    @focus="emit('focus')"
    @blur="emit('blur')"
  >
    <template #default="{ error }">
      <!-- Mantém apenas o outline de erro no componente do Vuetify -->
      <v-checkbox
        :id="props.forId"
        v-model="checkedComputed"
        v-bind="$attrs"
        :disabled="props.disabled"
        :readonly="props.readonly"
        density="compact"
        class="ma-0 pa-0"
        :error="!!error"
        :error-messages="[]"
        :hide-details="true"
        @focus="emit('focus')"
        @blur="emit('blur')"
      >
        <!-- Se quiser label custom com links, use o slot -->
        <template v-if="$slots.label" #label>
          <slot name="label" />
        </template>

        <!-- Label simples por prop -->
        <template v-else #label>
          {{ props.label }}
          <span v-if="props.required" aria-hidden="true">*</span>
        </template>
      </v-checkbox>
    </template>
  </FieldControl>
</template>
